import { errorIsStatus, GetPaths, useClientPortalAPI } from "@certn/api";
import { BasicButton, Text, Stack } from "@certn/cascadia";
import {
  ColumnDefinition,
  Container,
  DataTable,
  MediaQuery,
  Pagination,
  SectionMessage,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useQueryParams } from "../../hooks/useQueryParams";
import { Schemas } from "../../types";
import { queryParamsToApiParams } from "../utils";
import { ActionsDropdown } from "../settings/ActionsDropdown";
import { CustomQuestionnaireActionsDropdown } from "./custom-questionnaire/CustomQuestionnaireActionsDropdown";
import { getCommonTableColumns } from "./utils";
import { useFlags } from "launchdarkly-react-client-sdk";

type TemplatesEndpoint = Extract<
  GetPaths,
  | "/api/internal/client-portal/custom-questionnaire-templates/"
  | "/api/internal/client-portal/questionnaire-templates/"
>;

type TemplatesTableProps = {
  templateType: Schemas["TemplateTypeEnum"];
  title: string;
  description?: string;
  isReadonly?: boolean;
};

export const TemplatesTable = ({
  templateType,
  title,
  description,
  isReadonly = false,
}: TemplatesTableProps) => {
  const navigate = useNavigate();
  const { releaseCustomQuestionnaireTemplateBuilder } = useFlags();
  const { params, setParam } = useQueryParams();
  const { t } = useTranslation();

  const isCustom = templateType === "CUSTOM";
  const canReleaseCustomQuestionnaireTemplateBuilder =
    isCustom && releaseCustomQuestionnaireTemplateBuilder;

  const creationUrl = isCustom
    ? "/settings/custom-questionnaire-templates/create"
    : "/settings/questionnaire-templates/create";

  const endpoint: TemplatesEndpoint = isCustom
    ? "/api/internal/client-portal/custom-questionnaire-templates/"
    : "/api/internal/client-portal/questionnaire-templates/";
  const { data, isLoading, error, mutate } = useClientPortalAPI(endpoint, {
    ...queryParamsToApiParams(params),
  });

  const activeTemplates = data
    ? data.results.filter((template) => template.is_active === true)
    : [];

  const columns: ColumnDefinition<Schemas["QuestionnaireTemplatesList"]>[] =
    getCommonTableColumns<Schemas["QuestionnaireTemplatesList"]>({
      actions: ({ row }) =>
        isCustom ? (
          <CustomQuestionnaireActionsDropdown
            id={row.id}
            isActive={row.is_active}
            hideDeactivate={activeTemplates.length === 1}
            updateTemplates={mutate}
          />
        ) : (
          <ActionsDropdown question={row} count={activeTemplates?.length} />
        ),
      translate: t,
      hideActions: isReadonly,
    });

  return (
    <Container layout="full-width">
      <PageTitle
        actions={
          canReleaseCustomQuestionnaireTemplateBuilder ||
          templateType !== "CUSTOM" ? (
            <BasicButton
              testID="new-template-button"
              onPress={() => navigate(creationUrl)}
            >
              {t("New template")}
            </BasicButton>
          ) : undefined
        }
      >
        {title}
      </PageTitle>
      <MediaQuery sm type="hide">
        <Stack orientation="horizontal" gap="space-2">
          <Text color="text-weak">{description}</Text>
        </Stack>
      </MediaQuery>
      {error && !errorIsStatus(error, 404) && (
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t("Unable to retrieve questionnaire templates")}
        >
          {String(error.message)}
        </SectionMessage>
      )}
      <FilterBar testID="questionnaire-templates" hasSearch />
      <DataTable
        isLoading={isLoading}
        onSortChange={(sortDescriptor) =>
          setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
        }
        sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
        columns={columns}
        rows={data?.results ? data.results : []}
        aria-label={t("Questionnaire Templates Table")}
        testId="questionnaire-template-list-view-data-table"
        onRowAction={
          isReadonly
            ? undefined
            : (id) => {
                navigate(`${id}`);
              }
        }
      />
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={data?.count ?? 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
    </Container>
  );
};
